import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import ConfirmationModal from 'views_shared/app/javascript/components/ConfirmationModal';

/**
 * SelectionBar - Controls displayed when in conversation selection mode
 *
 * Shows: Close button, selected count, Select All/Deselect All button, and bulk action buttons
 */
function SelectionBar({
  selectedCount,
  totalCount,
  onClose,
  onSelectAll,
  onDeselectAll,
  onMarkRead,
  onArchive,
  isMarkingRead = false,
  isArchiving = false,
  hasPitchesSelected = false,
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const mountedRef = useRef(true);

  // Avoids calling setShowDeleteModal(false) after the component has already unmounted
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isLoading = isMarkingRead || isArchiving;

  const handleDeleteClick = () => {
    if (selectedCount === 0 || isLoading) return;
    setShowDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    await onArchive();
    if (mountedRef.current) setShowDeleteModal(false);
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center row-gap-2 column-gap-3 py-3 px-4 border-bottom border-neutral-20">
        {/* Close button */}
        <button
          type="button"
          className="btn btn-icon btn-secondary text-neutral-80"
          onClick={onClose}
          title="Cancel selection"
        >
          <i className="fa-solid fa-xmark" />
        </button>

        {/* Selected count */}
        <span className="paragraph-md">
          <strong>Selected:</strong> {selectedCount}
        </span>

        {/* Spacer (xl+ only — pushes actions to the right on a single row) */}
        <div className="flex-grow-1 d-none d-xl-block" />

        {/* Line break (under xl — forces action buttons onto a new row) */}
        <div className="w-100 d-xl-none" />

        {/* Action buttons group */}
        <div className="d-flex gap-2 align-items-center">
          {/* Select All / Deselect All button */}
          {allSelected ? (
            <button type="button" className="btn btn-secondary" style={{ height: '36px' }} onClick={onDeselectAll}>
              Deselect All
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" style={{ height: '36px' }} onClick={onSelectAll}>
              Select All
            </button>
          )}

          <button
            type="button"
            className="btn btn-icon btn-secondary text-neutral-80"
            onClick={onMarkRead}
            disabled={selectedCount === 0 || isLoading}
            title="Mark as read"
          >
            <i className="fa-regular fa-message" />
          </button>
          <button
            type="button"
            className="btn btn-icon btn-secondary text-neutral-80"
            onClick={handleDeleteClick}
            disabled={selectedCount === 0 || isLoading || hasPitchesSelected}
            title={hasPitchesSelected ? 'Delete is unavailable while pitches are selected' : 'Delete'}
          >
            <i className="fa-regular fa-trash" />
          </button>
        </div>
      </div>

      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmBulkDelete}
        title="Delete conversations"
        message={`Are you sure you want to delete ${selectedCount} conversation(s)?`}
        detail="These conversations will be moved to your Trash. Other participants will still see them."
        confirmLabel="Delete"
        confirmingLabel="Deleting..."
        isProcessing={isArchiving}
      />
    </>
  );
}

SelectionBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func.isRequired,
  onMarkRead: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  isMarkingRead: PropTypes.bool,
  isArchiving: PropTypes.bool,
  hasPitchesSelected: PropTypes.bool,
};

export default SelectionBar;
